import Guid from "common/values/guid/guid";
import moment, { Moment } from "moment";

export interface UserBookmarkedIndividualJSON {
  id: string;
  createdDate: string;
  individualId?: string;
}

export interface UserBookmarkedCompanyJSON {
  id: string;
  createdDate: string;
  companyId?: string;
}

export interface UserBookmarkedTeamJSON {
  id: string;
  createdDate: string;
  teamId?: string;
}

export default abstract class UserBookmark {
  public readonly id: Guid;
  public readonly createdDate: Date | string | Moment;
  public readonly entityId: Guid;
  public abstract toJSON(): UserBookmarkedIndividualJSON | UserBookmarkedCompanyJSON | UserBookmarkedTeamJSON;
  
  protected constructor(id: Guid, entityId: Guid, createdDate: Moment | Date) {
    this.id = id;
    this.entityId = entityId;
    this.createdDate = createdDate;
  }

  public static fromJSON(json: UserBookmarkedIndividualJSON | UserBookmarkedCompanyJSON | UserBookmarkedTeamJSON) {
    if (!json.id) {
      throw new InvalidUserBookmarkError('Invalid bookmark id.');
    }
    if (!json.createdDate) {
      throw new InvalidUserBookmarkError('Invalid bookmark created date.');
    }

    const id = Guid.fromJSON(json.id);
    const createdDate = moment(json.createdDate);

    if ('individualId' in json && json.individualId) {
      const individualId = Guid.fromJSON(json.individualId);
      return new UserBookmarkedIndividual(id, individualId, createdDate);
    }
    if ('companyId' in json && json.companyId) {
      const companyId = Guid.fromJSON(json.companyId);
      return new UserBookmarkedCompany(id, companyId, createdDate);
    }
    if ('teamId' in json && json.teamId) {
      const teamId = Guid.fromJSON(json.teamId);
      return new UserBookmarkedTeam(id, teamId, createdDate);
    }

    throw new InvalidUserBookmarkError('Invalid bookmark type. Must be one of: individualId, companyId, teamId.');
  }
}

export class UserBookmarkedIndividual extends UserBookmark {
  public readonly individualId: Guid;

  constructor(id: Guid, individualId: Guid, createdDate: Moment | Date) {
    super(id, individualId, createdDate);
    this.individualId = individualId;
  }

  public toJSON(): UserBookmarkedIndividualJSON {
    return {
      id: this.id.toJSON(),
      individualId: this.individualId.toJSON(),
      createdDate: moment(this.createdDate).toISOString()
    };
  }

  public static fromJSON(json: UserBookmarkedIndividualJSON): UserBookmarkedIndividual {
    if (!json.id) {
      throw new InvalidUserBookmarkError('Invalid bookmark id.');
    }
    if (!json.individualId) {
      throw new InvalidUserBookmarkError('Invalid bookmark individualId.');
    }
    if (!json.createdDate) {
      throw new InvalidUserBookmarkError('Invalid bookmark created date.');
    }
    const id = Guid.fromJSON(json.id);
    const individualId = Guid.fromJSON(json.individualId);
    const createdDate = moment(json.createdDate);
    return new UserBookmarkedIndividual(id, individualId, createdDate);
  }
}

export class UserBookmarkedCompany extends UserBookmark {
  public readonly companyId: Guid;

  constructor(id: Guid, companyId: Guid, createdDate: Moment | Date) {
    super(id, companyId, createdDate);
    this.companyId = companyId;
  }

  public toJSON(): UserBookmarkedCompanyJSON {
    return {
      id: this.id.toJSON(),
      companyId: this.companyId.toJSON(),
      createdDate: moment(this.createdDate).toISOString()
    };
  }

  public static fromJSON(json: UserBookmarkedCompanyJSON): UserBookmarkedCompany {
    if (!json.id) {
      throw new InvalidUserBookmarkError('Invalid bookmark id.');
    }
    if (!json.companyId) {
      throw new InvalidUserBookmarkError('Invalid bookmark companyId.');
    }
    if (!json.createdDate) {
      throw new InvalidUserBookmarkError('Invalid bookmark created date.');
    }
    const id = Guid.fromJSON(json.id);
    const companyId = Guid.fromJSON(json.companyId);
    const createdDate = moment(json.createdDate);
    return new UserBookmarkedCompany(id, companyId, createdDate);
  }
}

export class UserBookmarkedTeam extends UserBookmark {
  public readonly teamId: Guid;

  constructor(id: Guid, teamId: Guid, createdDate: Moment | Date) {
    super(id, teamId, createdDate);
    this.teamId = teamId;
  }

  public toJSON(): UserBookmarkedTeamJSON {
    return {
      id: this.id.toJSON(),
      teamId: this.teamId.toJSON(),
      createdDate: moment(this.createdDate).toISOString()
    };
  }

  public static fromJSON(json: UserBookmarkedTeamJSON): UserBookmarkedTeam {
    if (!json.id) {
      throw new InvalidUserBookmarkError('Invalid bookmark id.');
    }
    if (!json.teamId) {
      throw new InvalidUserBookmarkError('Invalid bookmark teamId.');
    }
    if (!json.createdDate) {
      throw new InvalidUserBookmarkError('Invalid bookmark created date.');
    }
    const id = Guid.fromJSON(json.id);
    const teamId = Guid.fromJSON(json.teamId);
    const createdDate = moment(json.createdDate);
    return new UserBookmarkedTeam(id, teamId, createdDate);
  }
}

export class InvalidUserBookmarkError extends Error {
  issues: unknown;

  constructor(issues: unknown) {
    super(`Invalid user bookmark info.`);
    this.issues = issues;
  }
}
